import { collabStateManager } from '@databases/collab-state-manager';
import { useDatabaseContent } from '@databases/database-state';
import { type AsyncOperation, AsyncOperationStatus } from '@databases/redux-types';
import { type DatabaseLayout } from '@databases/types/database/view';
import { useEffect } from 'react';

import {
  getFieldsCount,
  getFieldTypeCounts,
  getFieldTypes,
  getEntriesCount,
  getViewsCount
} from './database-size/resolvers.ts';
import { type DatabaseSizeMetrics } from './database-size/types.ts';
import {
  getDatabaseShareProgress,
  resolveDatabaseShareProgressReporter,
  type DatabaseShareProgress
} from './report-database-share-progress.ts';
import { useAnalytics } from './useAnalytics.ts';
import { getDatabaseContainerType } from './utils.ts';

const databasesWithCreatedEventFired = new Set<string>();
const SHARE_PROGRESS_DEBOUNCE_MS = 250;

let databaseSizeMetrics: DatabaseSizeMetrics = {
  contentId: false,
  entriesCount: false,
  fieldsCount: false,
  fieldTypeCounts: false,
  fieldTypes: false,
  viewsCount: false
};

export function getDatabaseSizeMetrics(): DatabaseSizeMetrics {
  return databaseSizeMetrics;
}

function setDatabaseSizeMetrics(metrics: DatabaseSizeMetrics): void {
  databaseSizeMetrics = {
    contentId: metrics.contentId,
    entriesCount: metrics.entriesCount,
    fieldsCount: metrics.fieldsCount,
    fieldTypeCounts: metrics.fieldTypeCounts,
    fieldTypes: metrics.fieldTypes,
    viewsCount: metrics.viewsCount
  };
}

function isSameDatabaseShareProgress(
  previous: DatabaseShareProgress | undefined,
  current: DatabaseShareProgress
): boolean {
  return (
    previous !== undefined &&
    previous.rows === current.rows &&
    previous.columns === current.columns &&
    previous.filledCells === current.filledCells
  );
}

export const useTrackDatabaseMetrics = ({
  databaseStatus,
  viewSettings
}: {
  databaseStatus: AsyncOperation<void> | undefined;
  viewSettings: DatabaseLayout;
}) => {
  const { contentId } = useDatabaseContent();
  const analytics = useAnalytics();

  const manager = collabStateManager.getCollabManager(contentId);

  useEffect(() => {
    if (
      !contentId ||
      databaseStatus?.status !== AsyncOperationStatus.SUCCEEDED ||
      !manager
    ) {
      return;
    }

    let disposed = false;
    let stopObservingShareProgress: (() => void) | undefined;
    let shareProgressTimeout: ReturnType<typeof setTimeout> | undefined;

    const fire = () => {
      const jsonData = manager.database.toJSON();

      const attributes = {
        persistedSnapshotSizeBytes: manager.initialPersistedSnapshotSizeBytes,
        fieldsCount: getFieldsCount(jsonData),
        fieldTypeCounts: getFieldTypeCounts(jsonData),
        fieldTypes: getFieldTypes(jsonData),
        entriesCount: getEntriesCount(jsonData),
        viewsCount: getViewsCount(jsonData),
        viewSettings: {
          layoutType: viewSettings.type,
          showNumberedEntriesEnabled: viewSettings.showNumberedEntries,
          cardWidth: viewSettings.cardWidth
        }
      };

      setDatabaseSizeMetrics({ ...attributes, contentId });

      const databaseContainer = getDatabaseContainerType();

      analytics.sendDatabaseProfiledEvent({
        contentId,
        databaseContainer,
        attributes
      });

      if (
        manager.wasCreatedThisSession &&
        !databasesWithCreatedEventFired.has(contentId)
      ) {
        databasesWithCreatedEventFired.add(contentId);
        analytics.sendDatabaseCreatedEvent({
          contentId,
          databaseContainer
        });
      }
    };

    const startShareProgressReporting = async () => {
      const reportShareProgress = await resolveDatabaseShareProgressReporter();

      if (disposed || !reportShareProgress) {
        return;
      }

      let lastReportedShareProgress: DatabaseShareProgress | undefined;

      const reportLatestShareProgress = () => {
        const progress = getDatabaseShareProgress(manager.database.toJSON());

        if (isSameDatabaseShareProgress(lastReportedShareProgress, progress)) {
          return;
        }

        lastReportedShareProgress = progress;
        reportShareProgress(progress);
      };

      const scheduleShareProgressReport = () => {
        clearTimeout(shareProgressTimeout);
        shareProgressTimeout = setTimeout(() => {
          shareProgressTimeout = undefined;
          reportLatestShareProgress();
        }, SHARE_PROGRESS_DEBOUNCE_MS);
      };

      reportLatestShareProgress();
      manager.database.doc.on('update', scheduleShareProgressReport);
      stopObservingShareProgress = () =>
        manager.database.doc.off('update', scheduleShareProgressReport);
    };

    fire();
    void startShareProgressReporting();

    return () => {
      disposed = true;
      stopObservingShareProgress?.();
      clearTimeout(shareProgressTimeout);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [databaseStatus?.status, contentId, manager]);
};
